// 医嘱任务相关API
import apiClient from './config'

/**
 * 医嘱任务API类
 */
export class MedicalOrderApi {
  /**
   * 查询所有医嘱任务模板列表
   * GET /medical-order-template/list
   * @returns {Promise<{success: boolean, message: string, data?: Array, errorCode?: number, raw?: object}>}
   */
  async getAllTemplates() {
    try {
      console.log('调用获取医嘱任务模板列表API')

      const response = await apiClient.get('/medical-order-template/list')
      const data = response.data || response
      console.log('医嘱任务模板列表API响应:', data)

      if (data.code === 0) {
        const list = Array.isArray(data.data) ? data.data : []
        // 做一次轻量格式化，保证字段存在
        const templates = list.map(item => ({
          id: item.id,
          taskName: item.taskName || '',
          taskContent: item.taskContent || '',
          createTime: item.createTime || '',
          updateTime: item.updateTime || ''
        }))

        return {
          success: true,
          message: data.message || '获取医嘱任务模板列表成功',
          data: templates,
          raw: data
        }
      }

      return {
        success: false,
        message: data.message || '获取医嘱任务模板列表失败',
        errorCode: data.code
      }
    } catch (error) {
      console.error('获取医嘱任务模板列表异常:', error)
      return {
        success: false,
        message: error.response?.data?.message || error.message || '获取医嘱任务模板列表失败'
      }
    }
  }

  /**
   * 新增医嘱任务模板
   * POST /medical-order-template/add
   * @param {Object} params
   * @param {string} params.taskName 任务名称（必填，≤30字）
   * @param {string} params.taskContent 任务内容（必填，≤500字）
   * @returns {Promise<{success: boolean, message: string, data?: boolean, errorCode?: number, raw?: object}>}
   */
  async addTemplate(params = {}) {
    const { taskName, taskContent } = params

    if (!taskName || !taskName.trim()) {
      return {
        success: false,
        message: '任务名称不能为空'
      }
    }

    const nameStr = taskName.trim()
    if (nameStr.length > 30) {
      return {
        success: false,
        message: '任务名称长度不能超过30字'
      }
    }

    if (!taskContent || !taskContent.trim()) {
      return {
        success: false,
        message: '任务内容不能为空'
      }
    }

    const contentStr = taskContent.trim()
    if (contentStr.length > 500) {
      return {
        success: false,
        message: '任务内容长度不能超过500字'
      }
    }

    const body = {
      taskName: nameStr,
      taskContent: contentStr
    }

    try {
      const response = await apiClient.post('/medical-order-template/add', body)
      const data = response.data || response

      if (data.code === 0) {
        return {
          success: true,
          message: data.message || '模板新增成功',
          data: data.data !== undefined ? data.data : true,
          raw: data
        }
      }

      return {
        success: false,
        message: data.message || '模板新增失败',
        errorCode: data.code
      }
    } catch (error) {
      console.error('新增医嘱任务模板异常:', error)
      return {
        success: false,
        message: error.response?.data?.message || error.message || '模板新增失败'
      }
    }
  }

  /**
   * 修改医嘱任务模板
   * PUT /medical-order-template/update
   * @param {Object} params
   * @param {number} params.id 模板ID（必填）
   * @param {string} params.taskName 新任务名称（必填，≤30字）
   * @param {string} params.taskContent 新任务内容（必填，≤500字）
   * @returns {Promise<{success: boolean, message: string, data?: boolean, errorCode?: number, raw?: object}>}
   */
  async updateTemplate(params = {}) {
    const { id, taskName, taskContent } = params

    if (!id && id !== 0) {
      return {
        success: false,
        message: '模板ID不能为空'
      }
    }

    if (!taskName || !taskName.trim()) {
      return {
        success: false,
        message: '任务名称不能为空'
      }
    }

    const nameStr = taskName.trim()
    if (nameStr.length > 30) {
      return {
        success: false,
        message: '任务名称长度不能超过30字'
      }
    }

    if (!taskContent || !taskContent.trim()) {
      return {
        success: false,
        message: '任务内容不能为空'
      }
    }

    const contentStr = taskContent.trim()
    if (contentStr.length > 500) {
      return {
        success: false,
        message: '任务内容长度不能超过500字'
      }
    }

    const body = {
      id: Number(id),
      taskName: nameStr,
      taskContent: contentStr
    }

    try {
      const response = await apiClient.put('/medical-order-template/update', body)
      const data = response.data || response

      if (data.code === 0) {
        return {
          success: true,
          message: data.message || '模板修改成功',
          data: data.data !== undefined ? data.data : true,
          raw: data
        }
      }

      return {
        success: false,
        message: data.message || '模板修改失败',
        errorCode: data.code
      }
    } catch (error) {
      console.error('修改医嘱任务模板异常:', error)
      return {
        success: false,
        message: error.response?.data?.message || error.message || '模板修改失败'
      }
    }
  }

  /**
   * 检查医嘱任务模板是否存在同名冲突
   * POST /medical-order-template/check
   * @param {Object} params
   * @param {string} params.taskName 任务名称（必填，≤30字）
   * @param {string} [params.taskContent] 任务内容（可选，≤500字）
   * @param {number} [params.id] 当前编辑模板ID（编辑时传，用于排除自身）
   * @returns {Promise<{success: boolean, message: string, data?: object, errorCode?: number, raw?: object}>}
   */
  async checkTemplate(params = {}) {
    const { taskName, taskContent, id } = params

    if (!taskName || !taskName.trim()) {
      return {
        success: false,
        message: '任务名称不能为空'
      }
    }

    const nameStr = taskName.trim()
    if (nameStr.length > 30) {
      return {
        success: false,
        message: '任务名称长度不能超过30字'
      }
    }

    const body = {
      taskName: nameStr
    }

    if (taskContent && taskContent.trim()) {
      const contentStr = taskContent.trim()
      if (contentStr.length > 500) {
        return {
          success: false,
          message: '任务内容长度不能超过500字'
        }
      }
      body.taskContent = contentStr
    }

    // 编辑场景下带上ID，后端可以排除自身
    if (id !== undefined && id !== null) {
      body.id = Number(id)
    }

    try {
      const response = await apiClient.post('/medical-order-template/check', body)
      const data = response.data || response

      if (data.code === 0) {
        return {
          success: true,
          message: data.message || '校验成功',
          data: data.data || null,
          raw: data
        }
      }

      return {
        success: false,
        message: data.message || '模板校验失败',
        errorCode: data.code
      }
    } catch (error) {
      console.error('校验医嘱任务模板异常:', error)
      return {
        success: false,
        message: error.response?.data?.message || error.message || '模板校验失败'
      }
    }
  }

  /**
   * 获取医生管理的所有患者医嘱记录列表
   * @param {Object} queryParams - 查询参数
   * @param {number} [queryParams.current] - 当前页码，默认1
   * @param {number} [queryParams.size] - 每页大小，默认10
   * @param {string} [queryParams.patientName] - 患者姓名（用于筛选）
   * @param {string} [queryParams.taskName] - 任务名称（用于筛选）
   * @param {number} [queryParams.completionStatus] - 完成状态（0-未完成，1-已完成）
   * @returns {Promise<Object>} 医嘱记录列表
   */
  async getDoctorMedicalOrderList(queryParams = {}) {
    try {
      console.log('调用获取医生医嘱记录列表API:', queryParams)

      // 构建请求参数
      const params = {
        current: queryParams.current || 1,
        size: queryParams.size || 10
      }

      // 添加可选的筛选参数
      if (queryParams.patientName) {
        params.patientName = queryParams.patientName
      }
      if (queryParams.taskName) {
        params.taskName = queryParams.taskName
      }
      if (queryParams.completionStatus !== undefined && queryParams.completionStatus !== null) {
        params.completionStatus = queryParams.completionStatus
      }

      console.log('构建的请求参数:', params)

      // 调用后端获取医嘱记录列表接口
      const response = await apiClient.get('/medical-order-record/doctor-list', {
        params
      })

      // 检查响应数据结构
      const data = response.data
      console.log('获取医嘱记录列表API响应详情:', data)
      console.log('响应状态码:', response.status)

      // 根据接口文档，code为0表示成功
      if (data.code === 0) {
        // 确保返回的数据格式符合前端需要
        const records = data.data?.records || []
        const total = data.data?.total || 0
        console.log('原始医嘱记录数据条数:', records.length)

        // 转换数据格式以适配前端显示
        const formattedRecords = records.map((record, index) => {
          console.log(`处理第${index + 1}个医嘱记录:`, record)

          // 格式化完成状态
          const formatCompletionStatus = (status) => {
            if (status === 1) {
              return '已完成'
            } else if (status === 0) {
              return '未完成'
            }
            return '未知状态'
          }

          // 格式化时间
          const formatDateTime = (dateTimeStr) => {
            if (!dateTimeStr) return ''
            try {
              const date = new Date(dateTimeStr)
              if (isNaN(date.getTime())) return dateTimeStr
              return date.toLocaleString('zh-CN', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
              })
            } catch (error) {
              console.warn('时间格式化失败:', dateTimeStr, error)
              return dateTimeStr
            }
          }

          return {
            id: record.id,
            doctorId: record.doctorId,
            patientId: record.patientId,
            patientName: record.patientName,
            taskName: record.taskName,
            taskContent: record.taskContent,
            completionTime: formatDateTime(record.completionTime),
            completionStatus: record.completionStatus, // 保留原始数字状态值
            status: formatCompletionStatus(record.completionStatus), // 格式化后的状态文本
            publishTime: formatDateTime(record.publishTime),
            updateTime: formatDateTime(record.updateTime),
            // 保留原始数据以便调试
            originalData: record
          }
        })

        console.log(`格式化医嘱记录完成，共${formattedRecords.length}条记录`)

        return {
          success: true,
          message: data.message || '获取医嘱记录列表成功',
          data: formattedRecords,
          total: total,
          current: queryParams.current || 1,
          size: queryParams.size || 10
        }
      } else {
        // 获取失败
        const errorMessage = data.message || '获取医嘱记录列表失败'
        console.warn('获取医嘱记录列表失败:', errorMessage)

        return {
          success: false,
          message: errorMessage,
          errorCode: data.code,
          data: [],
          total: 0
        }
      }
    } catch (error) {
      console.error('获取医嘱记录列表异常:', error)

      // 检查是否是网络错误
      if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
        return {
          success: false,
          message: '网络连接失败，请检查网络后重试',
          data: [],
          total: 0
        }
      }

      // 检查是否是认证错误
      if (error.response?.status === 401) {
        return {
          success: false,
          message: '登录已过期，请重新登录',
          data: [],
          total: 0
        }
      }

      // 检查是否是权限错误
      if (error.response?.status === 403) {
        return {
          success: false,
          message: '没有权限访问该资源',
          data: [],
          total: 0
        }
      }

      // 其他错误
      return {
        success: false,
        message: error.response?.data?.message || error.message || '获取医嘱记录列表失败，请重试',
        data: [],
        total: 0
      }
    }
  }

  /**
   * 新增医嘱任务记录
   * @param {Object} medicalOrderData - 医嘱任务数据
   * @param {number} medicalOrderData.patientId - 患者ID
   * @param {string} medicalOrderData.taskName - 任务名称
   * @param {string} medicalOrderData.taskContent - 任务内容
   * @param {string} medicalOrderData.completionTime - 完成时间（ISO格式）
   * @returns {Promise<Object>} 新增结果
   */
  async addMedicalOrderRecord(medicalOrderData) {
    try {
      console.log('调用新增医嘱任务记录API:', medicalOrderData)

      // 验证必填字段
      if (!medicalOrderData.patientId) {
        return {
          success: false,
          message: '患者ID不能为空'
        }
      }

      if (!medicalOrderData.taskName || !medicalOrderData.taskName.trim()) {
        return {
          success: false,
          message: '任务名称不能为空'
        }
      }

      if (!medicalOrderData.taskContent || !medicalOrderData.taskContent.trim()) {
        return {
          success: false,
          message: '任务内容不能为空'
        }
      }

      if (!medicalOrderData.completionTime) {
        return {
          success: false,
          message: '完成时间不能为空'
        }
      }

      // 构建请求数据
      const requestData = {
        patientId: parseInt(medicalOrderData.patientId),
        taskName: medicalOrderData.taskName.trim(),
        taskContent: medicalOrderData.taskContent.trim(),
        completionTime: medicalOrderData.completionTime,
        completionStatus: 0 // 新增时默认为未完成
      }

      console.log('构建的请求数据:', requestData)

      // 调用后端新增医嘱记录接口
      const response = await apiClient.post('/medical-order-record/add', requestData)

      // 检查响应数据结构
      const data = response.data
      console.log('新增医嘱任务记录API响应详情:', data)
      console.log('响应状态码:', response.status)

      // 根据接口文档，code为0表示成功
      if (data.code === 0) {
        return {
          success: true,
          message: data.message || '新增医嘱任务记录成功',
          data: data.data
        }
      } else {
        // 新增失败
        const errorMessage = data.message || '新增医嘱任务记录失败'
        console.warn('新增医嘱任务记录失败:', errorMessage)

        return {
          success: false,
          message: errorMessage,
          errorCode: data.code,
          data: null
        }
      }
    } catch (error) {
      console.error('新增医嘱任务记录异常:', error)

      // 检查是否是网络错误
      if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
        return {
          success: false,
          message: '网络连接失败，请检查网络后重试'
        }
      }

      // 检查是否是认证错误
      if (error.response?.status === 401) {
        return {
          success: false,
          message: '登录已过期，请重新登录'
        }
      }

      // 检查是否是权限错误
      if (error.response?.status === 403) {
        return {
          success: false,
          message: '没有权限执行此操作'
        }
      }

      // 其他错误
      return {
        success: false,
        message: error.response?.data?.message || error.message || '新增医嘱任务记录失败，请重试'
      }
    }
  }

  /**
   * 修改医嘱任务记录
   * @param {Object} medicalOrderData - 医嘱任务数据
   * @param {number} medicalOrderData.id - 医嘱记录ID
   * @param {number} medicalOrderData.doctorId - 医生ID
   * @param {number} medicalOrderData.patientId - 患者ID
   * @param {string} medicalOrderData.taskName - 任务名称
   * @param {string} medicalOrderData.taskContent - 任务内容
   * @param {string} medicalOrderData.completionTime - 完成时间（ISO格式）
   * @param {number} medicalOrderData.completionStatus - 完成状态（0-未完成，1-已完成）
   * @returns {Promise<Object>} 修改结果
   */
  async updateMedicalOrderRecord(medicalOrderData) {
    try {
      console.log('调用修改医嘱任务记录API:', medicalOrderData)

      // 验证必填字段
      if (!medicalOrderData.id) {
        return {
          success: false,
          message: '医嘱记录ID不能为空'
        }
      }

      if (!medicalOrderData.doctorId) {
        return {
          success: false,
          message: '医生ID不能为空'
        }
      }

      if (!medicalOrderData.patientId) {
        return {
          success: false,
          message: '患者ID不能为空'
        }
      }

      if (!medicalOrderData.taskName || !medicalOrderData.taskName.trim()) {
        return {
          success: false,
          message: '任务名称不能为空'
        }
      }

      if (!medicalOrderData.taskContent || !medicalOrderData.taskContent.trim()) {
        return {
          success: false,
          message: '任务内容不能为空'
        }
      }

      if (!medicalOrderData.completionTime) {
        return {
          success: false,
          message: '完成时间不能为空'
        }
      }

      if (medicalOrderData.completionStatus === undefined || medicalOrderData.completionStatus === null) {
        return {
          success: false,
          message: '完成状态不能为空'
        }
      }

      // 构建请求数据
      const requestData = {
        id: parseInt(medicalOrderData.id),
        doctorId: parseInt(medicalOrderData.doctorId),
        patientId: parseInt(medicalOrderData.patientId),
        taskName: medicalOrderData.taskName.trim(),
        taskContent: medicalOrderData.taskContent.trim(),
        completionTime: medicalOrderData.completionTime,
        completionStatus: parseInt(medicalOrderData.completionStatus)
      }

      console.log('构建的修改请求数据:', requestData)

      // 调用后端修改医嘱记录接口
      const response = await apiClient.put('/medical-order-record/update', requestData)

      // 检查响应数据结构
      const data = response.data
      console.log('修改医嘱任务记录API响应详情:', data)
      console.log('响应状态码:', response.status)

      // 根据接口文档，code为0表示成功
      if (data.code === 0) {
        return {
          success: true,
          message: data.message || '修改医嘱任务记录成功',
          data: data.data
        }
      } else {
        // 修改失败
        const errorMessage = data.message || '修改医嘱任务记录失败'
        console.warn('修改医嘱任务记录失败:', errorMessage)

        return {
          success: false,
          message: errorMessage,
          errorCode: data.code,
          data: null
        }
      }
    } catch (error) {
      console.error('修改医嘱任务记录异常:', error)

      // 检查是否是网络错误
      if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
        return {
          success: false,
          message: '网络连接失败，请检查网络后重试'
        }
      }

      // 检查是否是认证错误
      if (error.response?.status === 401) {
        return {
          success: false,
          message: '登录已过期，请重新登录'
        }
      }

      // 检查是否是权限错误
      if (error.response?.status === 403) {
        return {
          success: false,
          message: '没有权限执行此操作'
        }
      }

      // 其他错误
      return {
        success: false,
        message: error.response?.data?.message || error.message || '修改医嘱任务记录失败，请重试'
      }
    }
  }

  /**
   * 删除医嘱任务记录
   * @param {number} id - 医嘱记录ID
   * @returns {Promise<Object>} 删除结果
   */
  async deleteMedicalOrderRecord(id) {
    try {
      console.log('调用删除医嘱任务记录API:', id)

      // 验证必填字段
      if (!id) {
        return {
          success: false,
          message: '医嘱记录ID不能为空'
        }
      }

      console.log('删除医嘱记录ID:', id)

      // 调用后端删除医嘱记录接口
      const response = await apiClient.delete(`/medical-order-record/delete?id=${id}`)

      // 检查响应数据结构
      const data = response.data
      console.log('删除医嘱任务记录API响应详情:', data)
      console.log('响应状态码:', response.status)

      // 根据接口文档，code为0表示成功
      if (data.code === 0) {
        return {
          success: true,
          message: data.message || '删除医嘱任务记录成功',
          data: data.data
        }
      } else {
        // 删除失败
        const errorMessage = data.message || '删除医嘱任务记录失败'
        console.warn('删除医嘱任务记录失败:', errorMessage)

        return {
          success: false,
          message: errorMessage,
          errorCode: data.code,
          data: null
        }
      }
    } catch (error) {
      console.error('删除医嘱任务记录异常:', error)

      // 检查是否是网络错误
      if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
        return {
          success: false,
          message: '网络连接失败，请检查网络后重试'
        }
      }

      // 检查是否是认证错误
      if (error.response?.status === 401) {
        return {
          success: false,
          message: '登录已过期，请重新登录'
        }
      }

      // 检查是否是权限错误
      if (error.response?.status === 403) {
        return {
          success: false,
          message: '没有权限执行此操作'
        }
      }

      // 检查是否是404错误（记录不存在）
      if (error.response?.status === 404) {
        return {
          success: false,
          message: '要删除的医嘱记录不存在'
        }
      }

      // 其他错误
      return {
        success: false,
        message: error.response?.data?.message || error.message || '删除医嘱任务记录失败，请重试'
      }
    }
  }

  /**
   * 根据患者ID分页查询医嘱记录
   * @param {number} patientId - 患者ID
   * @param {Object} queryParams - 查询参数
   * @param {number} [queryParams.current] - 当前页码，默认1
   * @param {number} [queryParams.size] - 每页大小，默认10
   * @returns {Promise<Object>} 医嘱记录列表
   */
  async getPatientMedicalOrderList(patientId, queryParams = {}) {
    try {
      console.log('调用根据患者ID分页查询医嘱记录API:', { patientId, queryParams })

      // 验证必填参数
      if (!patientId) {
        return {
          success: false,
          message: '患者ID不能为空'
        }
      }

      // 构建请求参数
      const params = {
        current: queryParams.current || 1,
        size: queryParams.size || 10
      }

      console.log('构建的请求参数:', params)
      console.log('请求URL:', `/medical-order-record/doctor/patient/${patientId}`)

      // 调用后端根据患者ID分页查询医嘱记录接口
      const response = await apiClient.get(`/medical-order-record/doctor/patient/${patientId}`, {
        params
      })

      // 检查响应数据结构
      const data = response.data
      console.log('根据患者ID分页查询医嘱记录API响应详情:', data)
      console.log('响应状态码:', response.status)

      // 根据接口文档，code为0表示成功
      if (data.code === 0) {
        // 确保返回的数据格式符合前端需要
        const records = data.data?.records || []
        const total = data.data?.total || 0
        console.log('原始医嘱记录数据条数:', records.length)

        // 转换数据格式以适配前端显示
        const formattedRecords = records.map((record, index) => {
          console.log(`处理第${index + 1}个医嘱记录:`, record)

          // 格式化完成状态
          const formatCompletionStatus = (status) => {
            if (status === 1) {
              return '已完成'
            } else if (status === 0) {
              return '未完成'
            }
            return '未知状态'
          }

          // 格式化时间
          const formatDateTime = (dateTimeStr) => {
            if (!dateTimeStr) return ''
            try {
              const date = new Date(dateTimeStr)
              if (isNaN(date.getTime())) return dateTimeStr
              return date.toLocaleString('zh-CN', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
              })
            } catch (error) {
              console.warn('时间格式化失败:', dateTimeStr, error)
              return dateTimeStr
            }
          }

          return {
            id: record.id,
            doctorId: record.doctorId,
            patientId: record.patientId,
            patientName: record.patientName,
            taskName: record.taskName,
            taskContent: record.taskContent,
            completionTime: formatDateTime(record.completionTime),
            completionStatus: record.completionStatus, // 保留原始数字状态值
            status: formatCompletionStatus(record.completionStatus), // 格式化后的状态文本
            publishTime: formatDateTime(record.publishTime),
            updateTime: formatDateTime(record.updateTime),
            // 保留原始数据以便调试
            originalData: record
          }
        })

        console.log(`格式化医嘱记录完成，共${formattedRecords.length}条记录`)

        return {
          success: true,
          message: data.message || '获取患者医嘱记录列表成功',
          data: formattedRecords,
          total: total,
          current: queryParams.current || 1,
          size: queryParams.size || 10
        }
      } else {
        // 获取失败
        const errorMessage = data.message || '获取患者医嘱记录列表失败'
        console.warn('获取患者医嘱记录列表失败:', errorMessage)

        return {
          success: false,
          message: errorMessage,
          errorCode: data.code,
          data: [],
          total: 0
        }
      }
    } catch (error) {
      console.error('获取患者医嘱记录列表异常:', error)

      // 检查是否是网络错误
      if (error.code === 'NETWORK_ERROR' || error.message.includes('Network Error')) {
        return {
          success: false,
          message: '网络连接失败，请检查网络后重试',
          data: [],
          total: 0
        }
      }

      // 检查是否是认证错误
      if (error.response?.status === 401) {
        return {
          success: false,
          message: '登录已过期，请重新登录',
          data: [],
          total: 0
        }
      }

      // 检查是否是权限错误
      if (error.response?.status === 403) {
        return {
          success: false,
          message: '没有权限访问该资源',
          data: [],
          total: 0
        }
      }

      // 检查是否是404错误（患者不存在）
      if (error.response?.status === 404) {
        return {
          success: false,
          message: '患者不存在或没有医嘱记录',
          data: [],
          total: 0
        }
      }

      // 其他错误
      return {
        success: false,
        message: error.response?.data?.message || error.message || '获取患者医嘱记录列表失败，请重试',
        data: [],
        total: 0
      }
    }
  }

  /**
   * 格式化医嘱记录状态
   * @param {number} status - 状态值
   * @returns {string} 格式化后的状态文本
   */
  static formatCompletionStatus(status) {
    const statusMap = {
      0: '未完成',
      1: '已完成'
    }
    return statusMap[status] || '未知状态'
  }

  /**
   * 获取状态对应的标签类型
   * @param {string} status - 状态文本
   * @returns {string} Element Plus标签类型
   */
  static getStatusTagType(status) {
    const typeMap = {
      已完成: 'success',
      未完成: 'warning',
      未知状态: 'info'
    }
    return typeMap[status] || 'info'
  }
}

// 创建API实例
export const medicalOrderApi = new MedicalOrderApi()

// 默认导出
export default medicalOrderApi
